import {Box, Grid, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {SelectInputElement} from './SelectInputElement';
import {labelTextStyle, SwitchSegmentedYesNo, SwitchSegmentedYesNoBasic} from '../../../Core/UI';
import {observer} from 'mobx-react-lite';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {Prequal} from '../../../Core/mobx/ParameterStore/Prequal';
import {Health} from '../../../Core/mobx/ParameterStore/Health';
import HeightWeight from '../../Questions/HeightWeight';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/PrequalXref';
import {fieldOptions as healthFieldOptions} from '../../../Core/mobx/ParameterStore/HealthXref';
import {useEffect, useState} from 'react';
import {Button} from '@/components/ui/button';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

const useStyles = makeStyles({
    icon: {
        marginRight: 4,
    },
    approved: {
        color: 'green',
    },
    denied: {
        color: 'red',
    },
    sectionTitle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 46,
        width: 46,
        borderRadius: '50%',
        backgroundColor: '#e8edec',
        color: '#456d69',
        marginRight: 21,
        fontSize: 26,
        textAlign: 'center',
    },
    sectionTitleWrapper: {
        display: 'flex',
        alignItems: 'center',
        textTransform: 'capitalize',
    },
    keyHealthConditionsTitle: {
        marginTop: '1em',
        fontSize: 22,
        color: '#434343',
        fontWeight: 500,
        fontFamily: 'LoraSemiBold',
        lineHeight: '160%',
        letterSpacing: 0,
    },
    extraQuestionsBox: {
        margin: '56px 8px',
        boxShadow: '0px 0px 5px 0px #00000033',
    },
    extraQuestionsLabel: {
        marginBottom: 16,
        fontFamily: 'Fredoka',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: '100%',
    },
    dateLastUsedLabel: {
        marginBottom: 12,
        display: 'inline-flex',
        gap: 6,
    },
    noneOfTheAbove: {
        minWidth: 320,
        maxWidth: 600,
        flexShrink: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textTransform: 'capitalize',
        whiteSpace: 'normal',
        fontFamily: 'FredokaRegular',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: '160%',
        letterSpacing: 0,
        color: '#838383',
    },
    markAllButton: {
        width: 233,
        height: 48,
        textTransform: 'capitalize',
        fontFamily: 'FredokaRegular',
        fontSize: 16,
        lineHeight: '160%',
        letterSpacing: 0,
        color: '#838383',
        borderColor: '#838383',
        '&:hover': {
            backgroundColor: '#838383',
            color: 'white',
        },
    },
});

type HealthInfoProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Prequal;
    healthParameters: Health;
    hideNumbers?: boolean;
};

const HealthInfo = observer(({valueChanged, parameters, healthParameters, hideNumbers}: HealthInfoProps) => {
    const classes = useStyles();
    const [bipolarOrSchizophrenia, setBipolarOrSchizophrenia] = useState<boolean>(
        healthParameters.Schizophrenia.value || healthParameters.BipolarDisorder.value,
    );
    const [defibrillatorOrOrganTransplant, setDefibrillatorOrOrganTransplant] = useState<boolean>(
        healthParameters.Defibrillator.value || healthParameters.Transplant.value,
    );

    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    useEffect(() => {
        if (healthParameters.Schizophrenia.value || healthParameters.BipolarDisorder.value) {
            setBipolarOrSchizophrenia(true);
        } else if (
            typeof healthParameters.Schizophrenia.value == 'boolean' &&
            healthParameters.Schizophrenia.value == false &&
            typeof healthParameters.BipolarDisorder.value == 'boolean' &&
            healthParameters.BipolarDisorder.value == false
        ) {
            setBipolarOrSchizophrenia(false);
        }
    }, [healthParameters.Schizophrenia.value, healthParameters.BipolarDisorder.value]);

    useEffect(() => {
        if (healthParameters.Defibrillator.value || healthParameters.Transplant.value) {
            setDefibrillatorOrOrganTransplant(true);
        } else if (
            typeof healthParameters.Defibrillator.value == 'boolean' &&
            healthParameters.Defibrillator.value == false &&
            typeof healthParameters.Transplant.value == 'boolean' &&
            healthParameters.Transplant.value == false
        ) {
            setDefibrillatorOrOrganTransplant(false);
        }
    }, [healthParameters.Defibrillator.value, healthParameters.Transplant.value]);

    const setConditionsToNo = () => {
        setBipolarOrSchizophrenia(false);
        setDefibrillatorOrOrganTransplant(false);
        valueChanged(healthParameters.SleepApnea, false);
        valueChanged(healthParameters.sleepapneaSeverity, '');
        valueChanged(healthParameters.Cancer, false);
        valueChanged(healthParameters.cancerStage, '');
        valueChanged(healthParameters.WeightLossObesityGastric, false);
        valueChanged(healthParameters.HeartFailure, false);
        valueChanged(healthParameters.Stroke, false);
        valueChanged(healthParameters.HIVAIDS, false);
        valueChanged(healthParameters.BipolarDisorder, false);
        valueChanged(healthParameters.Schizophrenia, false);
        valueChanged(healthParameters.AtrialFibulationAfib, false);
        valueChanged(healthParameters.Defibrillator, false);
        valueChanged(healthParameters.Transplant, false);
    };

    return (
        <Grid container style={{marginBottom: 24}} spacing={2}>
            <Grid item xs={12}>
                <div id="prequal-eligibility-header" className={classes.sectionTitleWrapper}>
                    {!hideNumbers && <span className={classes.sectionTitle}>3</span>}
                    <Typography
                        style={{
                            fontSize: 24,
                            fontWeight: 700,
                            fontFamily: 'LoraSemiBold',
                            lineHeight: '120%',
                            letterSpacing: 0,
                        }}
                    >
                        Eligibility
                    </Typography>
                </div>
            </Grid>

            <div style={{width: '100%', marginBottom: 12}} />

            <HeightWeight parameters={parameters} valueChanged={valueChanged} prequal />

            <div style={{width: '100%', marginBottom: 24}} />

            <Grid item xs={4}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Tobacco/Marijuana Usage',
                        name: 'tobacco',
                        ...parameters.tobacco,
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                />
            </Grid>
            {parameters.tobacco.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>Tobacco/Marijuana Usage</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'What product did you use?',
                                name: 'tobaccoUsage',
                                options: fieldOptions.tobaccoUsage.options,
                                ...parameters.tobaccoUsage,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            newLabelType
                            showExtraLabel
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <Typography className={classes.dateLastUsedLabel} style={labelTextStyle}>
                            <span>{required()}</span>
                            <span>Date Last Used</span>
                        </Typography>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                                inputVariant="outlined"
                                variant="inline"
                                openTo="year"
                                views={['year', 'month']}
                                placeholder="MM/yyyy"
                                style={{width: '100%'}}
                                value={parameters.tobaccoUsageDate?.value || null}
                                maxDate={new Date()}
                                onChange={(date) => valueChanged(parameters.tobaccoUsageDate, date)}
                                InputProps={{
                                    style: {
                                        height: 48,
                                    },
                                }}
                                InputLabelProps={{
                                    shrink: !!parameters.tobaccoUsageDate?.value,
                                    style: parameters.tobaccoUsageDate?.value
                                        ? {}
                                        : {
                                              paddingLeft: 14,
                                              top: '50%',
                                              transform: 'translateY(-50%)',
                                          },
                                }}
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'Method used?',
                                name: 'tobaccoMethod',
                                options: fieldOptions.tobaccoMethod.options,
                                ...parameters.tobaccoMethod,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            newLabelType
                            showExtraLabel
                        />
                    </Grid>
                </Grid>
            )}
            <Grid item xs={12}>
                <div className={classes.keyHealthConditionsTitle}>Key Health Conditions</div>
            </Grid>
            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Have you had weight-loss surgery in the last 12 months?',
                        name: 'WeightLossObesityGastric',
                        ...healthParameters.WeightLossObesityGastric,
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Have you been diagnosed with sleep apnea?',
                        name: 'sleepApnea',
                        ...healthParameters.SleepApnea,
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            {healthParameters.SleepApnea.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>Sleep Apnea</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'Severity',
                                name: 'sleepapneaSeverity',
                                options: healthFieldOptions.sleepapneaSeverity.options,
                                ...healthParameters.sleepapneaSeverity,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            newLabelType
                            showExtraLabel
                        />
                    </Grid>
                </Grid>
            )}
            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Do you currently have cancer?',
                        name: 'Cancer',
                        ...healthParameters.Cancer,
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            {healthParameters.Cancer.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>Cancer</Typography>
                    </Grid>
                    <Grid item xs={10} sm={8} md={7} lg={5}>
                        <SelectInputElement
                            param={{
                                label: 'What stage of cancer did you have?',
                                name: 'cancerStage',
                                options: healthFieldOptions.cancerStage.options,
                                ...healthParameters.cancerStage,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            newLabelType
                            showExtraLabel
                        />
                    </Grid>
                </Grid>
            )}

            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Have you ever had a heart attack?',
                        name: 'HeartFailure',
                        ...healthParameters.HeartFailure,
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            {healthParameters.HeartFailure.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>Heart Attack</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                                inputVariant="outlined"
                                variant="inline"
                                openTo="year"
                                views={['year', 'month']}
                                label="When was your last heart attack?"
                                placeholder="MM/yyyy"
                                style={{width: '100%', marginTop: 8}}
                                value={healthParameters.heartAttackLastDate?.value || null}
                                maxDate={new Date()}
                                onChange={(date) => valueChanged(healthParameters.heartAttackLastDate, date)}
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                </Grid>
            )}
            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Have you ever had a stroke?',
                        name: 'Stroke',
                        ...healthParameters.Stroke,
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            {healthParameters.Stroke.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>Stroke</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                                inputVariant="outlined"
                                variant="inline"
                                openTo="year"
                                views={['year', 'month']}
                                label="When was your last stroke?"
                                placeholder="MM/yyyy"
                                style={{width: '100%', marginTop: 8}}
                                value={healthParameters.strokeDiagnosisDate?.value || null}
                                maxDate={new Date()}
                                onChange={(value) => valueChanged(healthParameters.strokeDiagnosisDate, value)}
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                </Grid>
            )}
            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Have you been diagnosed with HIV/AIDS?',
                        name: 'HIVAIDS',
                        ...healthParameters.HIVAIDS,
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Atrial Fibrillation (Afib)',
                        name: 'AtrialFibulationAfib',
                        ...healthParameters.AtrialFibulationAfib,
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchSegmentedYesNoBasic
                    value={bipolarOrSchizophrenia}
                    onChange={(value) => {
                        setBipolarOrSchizophrenia(value);
                        if (!value) {
                            // If setting to false, clear both individual conditions
                            valueChanged(healthParameters.BipolarDisorder, false);
                            valueChanged(healthParameters.Schizophrenia, false);
                        } else {
                            // If setting to true and neither is selected, default to setting at least one
                            // This ensures diagMentalDisorder will be true
                            if (!healthParameters.BipolarDisorder.value && !healthParameters.Schizophrenia.value) {
                                // We'll let the user choose which one, but ensure the flag is set
                                // The diagMentalDisorder will be set to true based on the mapping in recommendationUtils
                            }
                        }
                    }}
                    name="bipolarOrSchizophrenia"
                    label="Ever diagnosed with bipolar disorder or schizophrenia?"
                    required={required()}
                    horizontal
                />
            </Grid>
            {bipolarOrSchizophrenia && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>
                            Bipolar disorder or schizophrenia?
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <SwitchSegmentedYesNo
                            param={{
                                label: 'Bipolar disorder',
                                name: 'BipolarDisorder',
                                ...healthParameters.BipolarDisorder,
                            }}
                            required={required()}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <SwitchSegmentedYesNo
                            param={{
                                label: 'Schizophrenia',
                                name: 'Schizophrenia',
                                ...healthParameters.Schizophrenia,
                            }}
                            required={required()}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Grid item xs={4} />
                </Grid>
            )}

            <Grid item xs={12}>
                <SwitchSegmentedYesNoBasic
                    value={defibrillatorOrOrganTransplant}
                    onChange={(value) => {
                        setDefibrillatorOrOrganTransplant(value);
                        // If setting to false, clear both individual conditions
                        if (!value) {
                            valueChanged(healthParameters.Defibrillator, false);
                            valueChanged(healthParameters.Transplant, false);
                        }
                    }}
                    name="defibrillatorOrOrganTransplant"
                    label="Ever had a defibrillator or organ transplant?"
                    required={required()}
                    horizontal
                />
            </Grid>
            {defibrillatorOrOrganTransplant && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>
                            A Defibrillator Or Organ Transplant?
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <SwitchSegmentedYesNo
                            param={{
                                label: 'Defibrillator',
                                name: 'Defibrillator',
                                ...healthParameters.Defibrillator,
                            }}
                            required={required()}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <SwitchSegmentedYesNo
                            param={{
                                label: 'Organ Transplant',
                                name: 'Transplant',
                                ...healthParameters.Transplant,
                            }}
                            required={required()}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                </Grid>
            )}
            <Grid item xs={12} style={{padding: '0px 8px'}}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography className={classes.noneOfTheAbove}>
                        Client has none of the above key health conditions
                    </Typography>
                    <Button
                        variant="outline"
                        color="primary"
                        onClick={() => {
                            setConditionsToNo();
                        }}
                        className={classes.markAllButton}
                    >
                        Mark all as No
                    </Button>
                </Box>
            </Grid>
        </Grid>
    );
});

export default HealthInfo;
